import React, { useState, useEffect } from 'react';
import API from '../../services/api';

const PREDEFINED_LABELS = ['new-user', 'active', 'pending', 'resolved', 'high-priority', 'vip-request', 'follow-up'];

const LABEL_COLORS = {
  'new-user':      { bg: 'rgba(0,200,255,.1)',   border: 'rgba(0,200,255,.3)',   color: '#00C8FF' },
  'active':        { bg: 'rgba(0,255,136,.1)',   border: 'rgba(0,255,136,.3)',   color: '#00ff88' },
  'pending':       { bg: 'rgba(255,215,0,.1)',   border: 'rgba(255,215,0,.3)',   color: '#ffd700' },
  'resolved':      { bg: 'rgba(0,255,180,.1)',   border: 'rgba(0,255,180,.3)',   color: '#00ffb4' },
  'high-priority': { bg: 'rgba(255,50,50,.1)',   border: 'rgba(255,50,50,.3)',   color: '#ff6b6b' },
  'vip-request':   { bg: 'rgba(255,215,0,.1)',   border: 'rgba(255,215,0,.3)',   color: '#ffd700' },
  'follow-up':     { bg: 'rgba(200,100,255,.1)', border: 'rgba(200,100,255,.3)', color: '#c864ff' },
};

const getLabelStyle = (label) => LABEL_COLORS[label] || { bg: 'rgba(255,255,255,.06)', border: 'rgba(255,255,255,.15)', color: 'rgba(255,255,255,.6)' };

export default function UsersPage() {
  const [users, setUsers]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [labelFilter, setLabelFilter]   = useState('');
  const [allLabels, setAllLabels]       = useState(PREDEFINED_LABELS);
  const [editingLabels, setEditingLabels] = useState(null); // userId being edited
  const [newLabel, setNewLabel]         = useState('');
  const [customLabel, setCustomLabel]   = useState('');

  useEffect(() => {
    loadUsers();
    loadLabels();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await API.get('/users/all');
      setUsers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadLabels = async () => {
    try {
      const { data } = await API.get('/users/labels');
      setAllLabels(data.all || PREDEFINED_LABELS);
    } catch (err) {}
  };

  const handleBlock = async (userId, isBlocked) => {
    try {
      await API[isBlocked ? 'put' : 'put'](`/users/${userId}/${isBlocked ? 'unblock' : 'block'}`);
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, isBlocked: !isBlocked } : u));
    } catch (err) { console.error(err); }
  };

  const handleVip = async (userId, isVip) => {
    try {
      await API.put(`/users/${userId}/vip`, { isVip: !isVip });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, isVip: !isVip } : u));
    } catch (err) { console.error(err); }
  };

  const handleAddLabel = async (userId, label) => {
    const user = users.find(u => u._id === userId);
    if (!user || !label) return;
    const trimmed = label.trim().toLowerCase().replace(/\s+/g, '-');
    if (!trimmed) return;
    const newLabels = [...new Set([...(user.labels || []), trimmed])];
    try {
      await API.put(`/users/${userId}/labels`, { labels: newLabels });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, labels: newLabels } : u));
      if (!allLabels.includes(trimmed)) setAllLabels(prev => [...prev, trimmed]);
      setNewLabel('');
      setCustomLabel('');
    } catch (err) { console.error(err); }
  };

  const handleRemoveLabel = async (userId, label) => {
    const user = users.find(u => u._id === userId);
    if (!user) return;
    const newLabels = (user.labels || []).filter(l => l !== label);
    try {
      await API.put(`/users/${userId}/labels`, { labels: newLabels });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, labels: newLabels } : u));
    } catch (err) { console.error(err); }
  };

  // Filter users by search and label
  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase());
    const matchLabel = !labelFilter || (u.labels || []).includes(labelFilter);
    return matchSearch && matchLabel;
  });

  const getInitials = (name = '') => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Players</h1>
          <p style={s.subtitle}>{users.length} total users</p>
        </div>
      </div>

      {/* Search + Label Filter */}
      <div style={s.filters}>
        <input
          style={s.search}
          placeholder="🔍  Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={s.labelFilterWrap}>
          <button
            style={{ ...s.labelFilterBtn, ...(labelFilter === '' ? s.labelFilterBtnActive : {}) }}
            onClick={() => setLabelFilter('')}>
            All
          </button>
          {allLabels.map(label => {
            const ls = getLabelStyle(label);
            const isActive = labelFilter === label;
            return (
              <button key={label}
                style={{
                  ...s.labelFilterBtn,
                  ...(isActive ? { background: ls.bg, borderColor: ls.border, color: ls.color } : {}),
                }}
                onClick={() => setLabelFilter(isActive ? '' : label)}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Users list */}
      {loading ? (
        <div style={s.loading}>Loading users...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No users found</div>
      ) : (
        <div style={s.list}>
          {filtered.map(user => (
            <div key={user._id} style={s.userCard}>
              {/* User info */}
              <div style={s.userTop}>
                <div style={s.avatarWrap}>
                  <div style={s.avatar}>{getInitials(user.name)}</div>
                  {user.isOnline && <div style={s.onlineDot} />}
                </div>
                <div style={s.userInfo}>
                  <div style={s.userName}>
                    {user.name}
                    {user.isVip && <span style={s.vipBadge}>⭐ VIP</span>}
                    {user.isBlocked && <span style={s.blockedBadge}>🚫 Blocked</span>}
                  </div>
                  <div style={s.userEmail}>{user.email}</div>
                  <div style={s.userMeta}>
                    Joined {new Date(user.createdAt).toLocaleDateString()}
                    {user.lastSeen && ` · Last seen ${new Date(user.lastSeen).toLocaleDateString()}`}
                  </div>
                </div>
                {/* Action buttons */}
                <div style={s.actions}>
                  <button style={{ ...s.actionBtn, ...(user.isVip ? s.actionBtnActive : {}) }}
                    onClick={() => handleVip(user._id, user.isVip)}
                    title={user.isVip ? 'Remove VIP' : 'Make VIP'}>
                    ⭐
                  </button>
                  <button style={{ ...s.actionBtn, ...(user.isBlocked ? s.actionBtnDanger : {}) }}
                    onClick={() => handleBlock(user._id, user.isBlocked)}
                    title={user.isBlocked ? 'Unblock' : 'Block'}>
                    {user.isBlocked ? '🔓' : '🚫'}
                  </button>
                  <button style={{ ...s.actionBtn, ...(editingLabels === user._id ? s.actionBtnActive : {}) }}
                    onClick={() => setEditingLabels(editingLabels === user._id ? null : user._id)}
                    title="Manage Labels">
                    🏷️
                  </button>
                </div>
              </div>

              {/* Current labels */}
              {(user.labels || []).length > 0 && (
                <div style={s.labelsRow}>
                  {user.labels.map(label => {
                    const ls = getLabelStyle(label);
                    return (
                      <span key={label} style={{ ...s.labelChip, background: ls.bg, border: `1px solid ${ls.border}`, color: ls.color }}>
                        {label}
                        {editingLabels === user._id && (
                          <button style={s.labelRemoveBtn} onClick={() => handleRemoveLabel(user._id, label)}>✕</button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Label editor */}
              {editingLabels === user._id && (
                <div style={s.labelEditor}>
                  <div style={s.labelEditorTitle}>ADD LABEL</div>
                  {/* Predefined labels */}
                  <div style={s.predefinedLabels}>
                    {allLabels.filter(l => !(user.labels || []).includes(l)).map(label => {
                      const ls = getLabelStyle(label);
                      return (
                        <button key={label}
                          style={{ ...s.predefinedBtn, background: ls.bg, borderColor: ls.border, color: ls.color }}
                          onClick={() => handleAddLabel(user._id, label)}>
                          + {label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Custom label */}
                  <div style={s.customLabelRow}>
                    <input
                      style={s.customLabelInput}
                      placeholder="Custom label..."
                      value={customLabel}
                      onChange={e => setCustomLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { handleAddLabel(user._id, customLabel); } }}
                    />
                    <button style={s.customLabelBtn} onClick={() => handleAddLabel(user._id, customLabel)}>
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { flex: 1, overflowY: 'auto', padding: '28px', fontFamily: "'Exo 2',sans-serif", background: '#080c14' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { margin: 0, fontSize: '24px', fontWeight: '700', color: '#e2e8f0', fontFamily: "'Rajdhani',sans-serif", letterSpacing: '1px' },
  subtitle: { margin: '4px 0 0', color: 'rgba(255,255,255,.3)', fontSize: '13px' },
  filters: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' },
  search: { width: '100%', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '11px', padding: '11px 16px', color: '#fff', fontSize: '14px', fontFamily: "'Exo 2',sans-serif", outline: 'none', boxSizing: 'border-box' },
  labelFilterWrap: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  labelFilterBtn: { padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,.1)', background: 'transparent', color: 'rgba(255,255,255,.4)', cursor: 'pointer', fontSize: '12px', fontFamily: "'Exo 2',sans-serif", transition: 'all .15s' },
  labelFilterBtnActive: { background: 'rgba(0,255,180,.08)', borderColor: 'rgba(0,255,180,.3)', color: '#00ffb4' },
  loading: { textAlign: 'center', color: 'rgba(255,255,255,.3)', padding: '60px' },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,.2)', padding: '60px', fontSize: '14px' },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  userCard: { background: '#0d1420', borderRadius: '16px', border: '1px solid rgba(0,255,180,.08)', padding: '18px', transition: 'border-color .2s' },
  userTop: { display: 'flex', alignItems: 'flex-start', gap: '14px' },
  avatarWrap: { position: 'relative', flexShrink: 0 },
  avatar: { width: '46px', height: '46px', borderRadius: '13px', background: 'linear-gradient(135deg,#1e3a4a,#0d2233)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00ffb4', fontSize: '15px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif" },
  onlineDot: { position: 'absolute', bottom: '-1px', right: '-1px', width: '11px', height: '11px', borderRadius: '50%', background: '#00ffb4', border: '2px solid #0d1420', boxShadow: '0 0 6px #00ffb4' },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { color: '#e2e8f0', fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  userEmail: { color: 'rgba(255,255,255,.35)', fontSize: '12px', marginTop: '3px' },
  userMeta: { color: 'rgba(255,255,255,.2)', fontSize: '11px', marginTop: '3px' },
  vipBadge: { background: 'rgba(255,215,0,.12)', border: '1px solid rgba(255,215,0,.3)', color: '#ffd700', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: '700' },
  blockedBadge: { background: 'rgba(255,50,50,.1)', border: '1px solid rgba(255,50,50,.2)', color: '#ff6b6b', borderRadius: '6px', padding: '2px 8px', fontSize: '10px', fontWeight: '700' },
  actions: { display: 'flex', gap: '6px', flexShrink: 0 },
  actionBtn: { background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: '9px', color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: '16px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' },
  actionBtnActive: { background: 'rgba(0,255,180,.08)', borderColor: 'rgba(0,255,180,.25)', color: '#00ffb4' },
  actionBtnDanger: { background: 'rgba(255,50,50,.08)', borderColor: 'rgba(255,50,50,.2)', color: '#ff6b6b' },
  labelsRow: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' },
  labelChip: { borderRadius: '7px', padding: '3px 10px', fontSize: '11px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' },
  labelRemoveBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '11px', opacity: 0.7, padding: '0 2px', lineHeight: '1' },
  labelEditor: { marginTop: '14px', padding: '14px', background: 'rgba(0,255,180,.03)', borderRadius: '12px', border: '1px solid rgba(0,255,180,.08)' },
  labelEditorTitle: { color: 'rgba(0,255,180,.6)', fontSize: '10px', fontWeight: '700', letterSpacing: '2px', marginBottom: '10px' },
  predefinedLabels: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' },
  predefinedBtn: { border: '1px solid', borderRadius: '7px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: "'Exo 2',sans-serif", transition: 'opacity .15s' },
  customLabelRow: { display: 'flex', gap: '8px' },
  customLabelInput: { flex: 1, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,255,180,.15)', borderRadius: '9px', padding: '8px 12px', color: '#fff', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", outline: 'none' },
  customLabelBtn: { background: 'rgba(0,255,180,.1)', border: '1px solid rgba(0,255,180,.25)', borderRadius: '9px', color: '#00ffb4', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", fontWeight: '700' },
};
